import { CircularProgress, FormHelperText, Grid, MenuItem, Switch, TextField, Typography } from '@material-ui/core'
import React, { useEffect, useState } from 'react'
import FlexView from 'react-flexview/lib'
import { Controller, useFormContext } from 'react-hook-form'
import { PolicyModificationContextStore } from '../../../../context/PolicyModificationContext'
import { FirebaseConfigKey, FirebaseService } from '../../../../services/FirebaseService'
import LocalizationService from '../../../../services/LocalizationService'
import { getEstimatedMilesOptions, prange, vinValidation } from '../../../../services/utils'
import { useAdministrativeDivisions } from '../../../Core/Hooks/useAdministrativeDivision'
import { GlobalTextField } from '../../../Core/Input/ControlledTextField'
import { snwAtvQuestionAdjustments, validateOwnershipMethod } from '../../../Motorcycle/motorcyclesUtils'
import './MotorcycleSection.scss'
import { MotorcyclesYearMakeModelSection } from '../../../Motorcycle/Sections/MotorcyclesYearMakeModelSection'
import { MotorcyclesYesNoQuestions } from '../../../Motorcycle/Sections/MotorcyclesYesNoQuestions'
import { MotorcycleLienholderSection } from '../../../Motorcycle/Sections/MotorcycleLienholderSection'

export const MotorcyclesDetailsSection = ({ isLojackFactordDisabled, needDisabled = false, pushOther = true }) => {
  const methods = useFormContext()
  const [isAntiLockFactorDisabled, setIsAntiLockFactorDisabled] = useState(false)
  const [isNeedOdometerCheck, setIsNeedOdometerCheck] = useState(false)
  const { isLoadingStates, isErrorStates, administrativeDivisions: states } = useAdministrativeDivisions()
  const typeWatch = methods.watch('type')

  const setStateFromAutocomplete = autoState => {
    let selected = states.filter(i => i.name.toLowerCase() == autoState.toLowerCase())
    if (selected.length > 0) methods.setValue('state', selected[0].code)
  }

  const validateOwnership = ownershipPeriod => {
    const year = methods.getValues()['year']
    if (year) {
      return validateOwnershipMethod(ownershipPeriod, year)
    }
  }

  useEffect(() => {
    methods.register({ name: 'new_vehicle_req_details' })
    methods.register({ name: 'type' })
    methods.register({ name: 'make' })
    methods.register({ name: 'model' })
    methods.register({ name: 'lienholde_details' })
    //Check if needed:
    methods.register({ name: 'engine_cc' })
    methods.register({ name: 'is_motorcycle_include_odometer' })
  }, [])

  useEffect(() => {
    let res = snwAtvQuestionAdjustments(typeWatch)
    setIsAntiLockFactorDisabled(res.isAntiLockFactorDisabled)
    setIsNeedOdometerCheck(res.isNeedOdometerCheck)
  }, [typeWatch])

  return (
    <React.Fragment>
      <div className="text-title bold black margin-bottom-basic blue">Motorcycle Details</div>
      <Grid container spacing={3}>
        <MotorcyclesYearMakeModelSection needDisabled={needDisabled} pushOther={pushOther} />
        <Grid item xs={12} md={4}>
          <Typography>Zip Code</Typography>
          <GlobalTextField
            fullWidth
            name="zip_code"
            register={methods.register}
            errors={methods.errors}
            helperText={!!methods.errors.zip_code && 'Required'}
            inputRef={methods.register({ required: true })}
          />
        </Grid>
        {(!methods.getValues()['year'] || methods.getValues()['year'] != 'yeardefault') && (
          <Grid item xs={12} md={4}>
            <Typography>Ownership Period</Typography>
            <Controller
              control={methods.control}
              rules={{ validate: validateOwnership, required: true }}
              name="ownership_period"
              defaultValue={methods.control.defaultValuesRef.current.ownership_period || 'ownershipDefault'}
              as={
                <TextField
                  InputLabelProps={{ className: 'avenir-book' }}
                  error={!!methods.errors.ownership_period}
                  helperText={!!methods.errors.ownership_period && 'Required'}
                  variant="outlined"
                  fullWidth
                  select
                >
                  <MenuItem disabled value={'ownershipDefault'}>
                    <div className="placeholder-text avenir-book">Ownership period</div>
                  </MenuItem>
                  <MenuItem value={0}>Less than 1 year ago</MenuItem>
                  {prange(1, 11, 1).map(val => (
                    <MenuItem value={val}>{val}</MenuItem>
                  ))}
                  <MenuItem value={11}>11+</MenuItem>
                </TextField>
              }
            />
          </Grid>
        )}
        <Grid item xs={12} md={4}>
          <Typography>VIN</Typography>
          <GlobalTextField
            fullWidth
            disabled={needDisabled}
            name="vin"
            rules={{ required: true }}
            onChange={vinValidation}
            register={methods.register}
            errors={methods.errors}
            helperText={!!methods.errors.vin && (methods.errors.vin.type == 'required' ? 'Required' : 'Vin must contain 17 characters')}
            inputRef={methods.register({ required: true, maxLength: 17, minLength: 17 })}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <Typography>{'Accessory Value (optional)'}</Typography>
          <Controller
            control={methods.control}
            name="accessory_value"
            defaultValue={methods.control.defaultValuesRef.current.accessory_value || 0}
            as={
              <TextField
                InputLabelProps={{ className: 'avenir-book' }}
                error={!!methods.errors.accessory_value}
                helperText={!!methods.errors.accessory_value && 'Required'}
                variant="outlined"
                fullWidth
                select
              >
                <MenuItem disabled value={0}>
                  <div className="placeholder-text">Accessory Value</div>
                </MenuItem>
                {prange(0, 11000, 1000).map(val => (
                  <MenuItem key={val} value={val}>
                    {FirebaseService.getValue(FirebaseConfigKey.CURRENCY_SYMBOL)}
                    {val.toLocaleString()}
                  </MenuItem>
                ))}
              </TextField>
            }
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <Typography>{'Sidecar value (optional)'}</Typography>
          <Controller
            control={methods.control}
            name="side_car_value"
            defaultValue={methods.control.defaultValuesRef.current.side_car_value || 0}
            as={
              <TextField
                InputLabelProps={{ className: 'avenir-book' }}
                error={!!methods.errors.side_car_value && 'Required'}
                variant="outlined"
                fullWidth
                select
              >
                <MenuItem disabled value={0}>
                  <div className="placeholder-text">Sidecar Value</div>
                </MenuItem>
                {prange(0, 11000, 1000).map(val => (
                  <MenuItem value={val}>
                    {FirebaseService.getValue(FirebaseConfigKey.CURRENCY_SYMBOL)}
                    {val.toLocaleString()}
                  </MenuItem>
                ))}
              </TextField>
            }
          />
        </Grid>
        <Grid item xs={12}>
          <FlexView className="switch_question_style">
            <Typography>How many miles do you estimate to ride annually on this vehicle?</Typography>
            <Controller
              control={methods.control}
              name="estimated_mileage"
              rules={{ validate: x => x !== 0, required: true }}
              defaultValue={methods.control.defaultValuesRef.current.side_car_value || 0}
              as={
                <TextField
                  InputLabelProps={{ className: 'avenir-book' }}
                  // rules={{ validate: x => x !== 0, required: true }}
                  error={!!methods.errors.estimated_mileage && 'Required'}
                  variant="outlined"
                  select
                  size={'medium'}
                >
                  <MenuItem disabled value={0}>
                    <div className="placeholder-text">Annual miles estimation</div>
                  </MenuItem>
                  {getEstimatedMilesOptions().estimatedMileageOption.map(val => (
                    <MenuItem value={val}>{val.toString()}</MenuItem>
                  ))}
                </TextField>
              }
            />
          </FlexView>
        </Grid>
        <MotorcyclesYesNoQuestions
          isLojackFactordDisabled={isLojackFactordDisabled}
          isNeedOdometerCheck={isNeedOdometerCheck}
          isAntiLockFactorDisabled={isAntiLockFactorDisabled}
        />
        {methods.watch('has_lienholder') && (
          <MotorcycleLienholderSection
            isLoadingStates={isLoadingStates}
            states={states}
            setStateFromAutocomplete={setStateFromAutocomplete}
          />
        )}
      </Grid>
    </React.Fragment>
  )
}
